import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
  ArrowLeft,
  Check,
  ChevronsUpDown,
  Loader2,
  PencilLine,
} from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import { Button } from "@app/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@app/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@app/components/ui/popover";
import { CorrectionsPanel } from "@app/components/CorrectionsPanel";
import { useViewAsKey } from "@app/services/view-as";
import { usePaginatedSearch } from "@app/utils/use-paginated-search";
import {
  fetchDatasetPickerPage,
  type DatasetPickerItem,
} from "@app/utils/dataset-picker";

/**
 * The corrections read, at the scale it actually happens.
 *
 * It lives in the laboratory rather than inside a dataset's tabs, and the reason is a
 * measurement rather than a preference: 93 % of production's corrections are written on
 * records that belong to no dataset at all. A dataset tab is still offered — the same
 * panel, pinned to that dataset's patients — but it can only ever show a slice, and the
 * corpus-wide reading is the one the ontology work is steered by.
 *
 * The dataset selector below is what keeps the two one click apart instead of two screens.
 */

export default function LaboratoireCorrectionsPage() {
  const { t } = useLingui();
  // Whose platform is on screen — the whole preview, not two of its three parts. Switching
  // between two doctors used to leave this page on the first one's corrections.
  const viewAsKey = useViewAsKey();
  // null: every dossier the reader may see.
  const [scope, setScope] = useState<DatasetPickerItem | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const picker = usePaginatedSearch({
    open: pickerOpen,
    fetchPage: fetchDatasetPickerPage,
  });
  const search = picker.searchValue.trim();

  // The picker reads its list when it opens; a dataset chosen under another speciality or
  // previewed account may not belong to this one.
  useEffect(() => {
    setScope(null);
  }, [viewAsKey]);

  return (
    <div className="p-6">
      <Link
        to="/laboratoire"
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        <Trans>Retour au laboratoire</Trans>
      </Link>

      <div className="mb-6 mt-2 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold text-slate-900">
            <PencilLine className="h-7 w-7 text-primary" />
            <Trans>Corrections</Trans>
          </h1>
          <p className="mt-1 max-w-2xl text-slate-600">
            <Trans>
              Le journal des passages du crayon, lu par champ : ce que les médecins
              reprennent, dans quelle proportion, et — depuis que la question leur
              est posée — pour quelle raison.
            </Trans>
          </p>
        </div>

        <div className="w-64">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={pickerOpen}
                aria-label={t`Restreindre à un jeu de données`}
                className="w-full justify-between border-slate-200 font-normal text-slate-700"
              >
                <span className="truncate">
                  {scope ? (
                    scope.name
                  ) : (
                    <Trans>Tous les dossiers que je peux voir</Trans>
                  )}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0 border-slate-200">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder={t`Rechercher un jeu de données...`}
                  value={picker.searchValue}
                  onValueChange={picker.setSearchValue}
                />
                <CommandList onScroll={picker.onScroll}>
                  {!picker.loading && (
                    <CommandEmpty>
                      <Trans>
                        Aucun jeu de données ne correspond à votre recherche
                        «&nbsp;{search}&nbsp;»
                      </Trans>
                    </CommandEmpty>
                  )}
                  <CommandGroup>
                    {!search && (
                      <CommandItem
                        value="all"
                        onSelect={() => {
                          setScope(null);
                          setPickerOpen(false);
                        }}
                      >
                        <Check
                          className={`mr-2 h-4 w-4 shrink-0 ${scope ? "opacity-0" : "opacity-100"}`}
                        />
                        {/* Neither "all corrections" nor "outside datasets": the scope INCLUDES
                            dataset patients, and the visibility filter already restricts it to
                            what the reader may open — their speciality, for a reviewer. */}
                        <Trans>Tous les dossiers que je peux voir</Trans>
                      </CommandItem>
                    )}
                    {picker.items.map((dataset) => (
                      <CommandItem
                        key={dataset.id}
                        value={dataset.id}
                        onSelect={() => {
                          setScope(dataset);
                          setPickerOpen(false);
                        }}
                      >
                        <Check
                          className={`mr-2 h-4 w-4 shrink-0 ${scope?.id === dataset.id ? "opacity-100" : "opacity-0"}`}
                        />
                        <span className="truncate">{dataset.name}</span>
                      </CommandItem>
                    ))}
                    {picker.loading && (
                      <div className="flex items-center justify-center py-2 text-slate-400">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      </div>
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <CorrectionsPanel
        key={`${scope?.id ?? "all"}|${viewAsKey}`}
        datasetId={scope?.id}
        embedded
      />
    </div>
  );
}
